import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// All RevQ API traffic goes through our FastAPI proxy at /api/proxy/*,
// which transparently forwards to https://ehi.ariaone.ai/*.
export const api = axios.create({
  baseURL: `${BACKEND}/api/proxy`,
  timeout: 90_000,
});

// ── ETag / conditional-GET cache (Phase 3) ──────────────────────────────────
// Opt-in per call via `{ etag: true }` in the axios config. For those requests we
// remember the ETag + body; the next identical request sends `If-None-Match`, and a
// 304 (empty body) is transparently resolved with the cached body. Everything that
// does NOT pass `etag:true` is completely unaffected. Used for interval polls.
const etagCache = new Map();
const etagKey = (cfg) => `${(cfg.method || "get").toLowerCase()}:${cfg.url}:${JSON.stringify(cfg.params || {})}`;

const MODULE_BY_PATH = {
  "/home": "home",
  "/patients": "patients",
  "/telehealth-v2": "telehealth",
  "/encounter-review": "encounter-review",
  "/dictation": "dictation",
  "/patient-documents": "chart-documents",
  "/clinic-documents": "clinic-documents",
  "/create-claims": "claims-queue",
  "/payments-v2": "payment-posting",
  "/lumina-documents": "lumina-documents",
  "/luminaATwork": "lumina-at-work",
};

function currentModule() {
  try {
    const p = window.location.pathname;
    if (MODULE_BY_PATH[p]) return MODULE_BY_PATH[p];
    const hit = Object.keys(MODULE_BY_PATH).find((k) => p.startsWith(k));
    return hit ? MODULE_BY_PATH[hit] : (p.split("/")[1] || "");
  } catch { return ""; }
}

// Attach the bearer token on every request, if we have one.
api.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem("revq.token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  const m = currentModule();
  if (m) cfg.headers["X-RevQ-Module"] = m;
  if (cfg.etag && (cfg.method || "get").toLowerCase() === "get") {
    // Only these opt-in requests accept a 304 (default axios rejects it); scoped per-request.
    cfg.validateStatus = (s) => (s >= 200 && s < 300) || s === 304;
    const hit = etagCache.get(etagKey(cfg));
    if (hit?.etag) cfg.headers["If-None-Match"] = hit.etag;
  }
  return cfg;
});

// On 401 the upstream JWT is dead — bounce back to /login.
api.interceptors.response.use(
  (r) => {
    const cfg = r.config || {};
    if (cfg.etag) {
      const key = etagKey(cfg);
      if (r.status === 304) {
        const hit = etagCache.get(key);
        if (hit) r.data = hit.data;        // serve cached body (304 has none)
      } else {
        const et = r.headers?.etag || r.headers?.ETag;
        if (et) etagCache.set(key, { etag: et, data: r.data });
      }
    }
    return r;
  },
  (err) => {
    if (err?.response?.status === 401) {
      sessionStorage.removeItem("revq.token");
      sessionStorage.removeItem("revq.user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  },
);

// Helper that surfaces the upstream's structured validation errors.
export function describeError(err) {
  const r = err?.response?.data;
  if (!r) return err?.message || "Network error";
  if (typeof r === "string") return r;
  if (r.detail && typeof r.detail === "string") return r.detail;
  if (Array.isArray(r.detail)) {
    return r.detail
      .map((d) => `${(d.loc || []).slice(-1).join(".")}: ${d.msg}`)
      .join(" · ");
  }
  if (Array.isArray(r.issues)) return r.issues.map((i) => i.message || i).join(" · ");
  return JSON.stringify(r);
}
